import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApolloQueryResult, FetchPolicy, WatchQueryFetchPolicy } from '@apollo/client';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { cloneDeep, get } from 'lodash';

import { AppApolloService, CredentialsService, I18nService, throwOnGraphqlError } from '@app/core';
import { EmailTemplates, SignatureFormat, User } from '@app/models';
import { NotyService } from '@app/services/noty.service';
import {
  Contact,
  ContactsGQL,
  ContactsQueryVariables,
  CurrentUserCreditsGQL,
  CurrentUserGQL,
  CurrentUserOrganizationGQL,
  CurrentUserQuery,
  NotificationsInfoGQL,
  SetCurrentUserMissingInfoGQL,
  SetCurrentUserMissingInfoMutationVariables,
  UpdateApiOptionsGQL,
  UpdateApiOptionsMutationVariables,
  UpdateCurrentUserGQL,
  UpdateCurrentUserInitialsGQL,
  UpdateCurrentUserInitialsMutationVariables,
  UpdateCurrentUserLocaleGQL,
  UpdateCurrentUserLocaleMutationVariables,
  UpdateCurrentUserMutationVariables,
  UpdateCurrentUserPasswordGQL,
  UpdateCurrentUserPasswordMutationVariables,
  UpdateCurrentUserSignatureEmailGQL,
  UpdateCurrentUserSignatureEmailMutationVariables,
  UpdateCurrentUserSignatureGQL,
  UpdateCurrentUserSignatureMutationVariables,
  UpdateUserDeveloperFlagGQL,
  UpdateUserDeveloperFlagMutationVariables,
  UpdateNotificationsInfoGQL,
  UpdateNotificationsInfoMutationVariables,
  UpdateUserAdditionalConfigsMutationVariables,
  UpdateUserAdditionalConfigsGQL,
  UpdateUserPasswordGQL,
  UpdateUserPasswordMutationVariables
} from 'src/generated/graphql.default';

export type CustomContact = Contact & { _isSelected?: boolean };
export interface SimpleUserWithOrganization {
  company?: string;
  cnpj?: string;
  member?: {
    group?: {
      company?: string;
      cnpj?: string;
      styles?: { overwrite_name?: boolean };
    };
  };
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private _watchCurrentUser$: Observable<User>;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private appApolloService: AppApolloService,
    private credentialsService: CredentialsService,
    private notyService: NotyService,
    private currentUserGQL: CurrentUserGQL,
    private currentUserOrganizationGQL: CurrentUserOrganizationGQL,
    private currentUserCreditsGQL: CurrentUserCreditsGQL,
    private contactsGQL: ContactsGQL,
    private updateCurrentUserGQL: UpdateCurrentUserGQL,
    private updateUserDeveloperFlagGQL: UpdateUserDeveloperFlagGQL,
    private updateCurrentUserPasswordGQL: UpdateCurrentUserPasswordGQL,
    private updateCurrentUserSignatureGQL: UpdateCurrentUserSignatureGQL,
    private updateCurrentUserInitialsGQL: UpdateCurrentUserInitialsGQL,
    private updateCurrentUserSignatureEmailGQL: UpdateCurrentUserSignatureEmailGQL,
    private setCurrentUserMissingInfoGQL: SetCurrentUserMissingInfoGQL,
    private notificationsInfoGQL: NotificationsInfoGQL,
    private updateNotificationsInfoGQL: UpdateNotificationsInfoGQL,
    private updateApiOptionsGQL: UpdateApiOptionsGQL,
    private updateCurrentUserLocaleGQL: UpdateCurrentUserLocaleGQL,
    private updateUserAdditionalConfigsGQL: UpdateUserAdditionalConfigsGQL,
    private updateUserPasswordGQL: UpdateUserPasswordGQL
  ) {}

  getCurrentUser(options: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'cache-first' }): Observable<User> {
    return this.currentUserGQL.fetch(null, options).pipe(
      throwOnGraphqlError(),
      map(response => this.parseUser(response)),
      tap(user => (this.i18nService.locale = user.locale))
    );
  }

  watchCurrentUser(options?: { fetchPolicy: WatchQueryFetchPolicy }): Observable<User> {
    if (!options && this._watchCurrentUser$) return this._watchCurrentUser$;

    const watchCurrentUser$ = this.currentUserGQL.watch(null, { fetchPolicy: 'cache-first', ...(options || {}) }).valueChanges;
    return (this._watchCurrentUser$ = this.credentialsService.onChangeCredentials().pipe(
      filter(credentials => !!credentials),
      switchMap(() => watchCurrentUser$),
      throwOnGraphqlError(),
      map(response => this.parseUser(response)),
      tap(user => (this.i18nService.locale = user.locale)),
      shareReplay(1)
    ));
  }

  currentUserOrganization() {
    return this.currentUserOrganizationGQL.fetch(null).pipe(map(response => response.data.me.organization));
  }

  currentUserCredits() {
    return this.currentUserCreditsGQL.fetch(null).pipe(map(response => response.data.me.subscription));
  }

  notificationsInfo() {
    return this.notificationsInfoGQL.fetch(null).pipe(map(response => response.data.notifications));
  }

  contacts(variables: ContactsQueryVariables, options: { fetchPolicy: FetchPolicy } = { fetchPolicy: 'cache-first' }) {
    return this.contactsGQL.fetch(variables, options).pipe(
      throwOnGraphqlError(),
      map(response => response.data.contacts?.data)
    );
  }

  updateCurrentUser(variables: UpdateCurrentUserMutationVariables) {
    variables.user.name = (variables.user.name || '').trim();
    if (variables.user.organization) {
      variables.user.organization.name = (variables.user.organization.name || '').trim() || null;
      // variables.user.organization.cnpj = (variables.user.organization.cnpj || '').trim() || null;
    }
    if (variables.user.email) {
      variables.user.email = (variables.user.email || '').trim();
    }

    return this.updateCurrentUserGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUser)
    );
  }

  updateCurrentUserPassword(variables: UpdateCurrentUserPasswordMutationVariables) {
    return this.updateCurrentUserPasswordGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.changeUserPassword)
    );
  }

  updateCurrentUserSignature(variables: UpdateCurrentUserSignatureMutationVariables) {
    return this.updateCurrentUserSignatureGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserSignature),
      tap(user => this.appApolloService.forceUpdateCurrentUserCache({ me: user }))
    );
  }

  updateCurrentUserInitials(variables: UpdateCurrentUserInitialsMutationVariables) {
    return this.updateCurrentUserInitialsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserInitials),
      tap(user => this.appApolloService.forceUpdateCurrentUserCache({ me: user }))
    );
  }

  updateCurrentUserSignatureEmail(variables: UpdateCurrentUserSignatureEmailMutationVariables) {
    return this.updateCurrentUserSignatureEmailGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserSignatureEmail),
      tap(user => this.appApolloService.forceUpdateCurrentUserCache({ me: user }))
    );
  }

  updateCurrentUserDeveloperFlag(variables: UpdateUserDeveloperFlagMutationVariables) {
    return this.updateUserDeveloperFlagGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserDeveloperFlag),
      tap(user => this.appApolloService.forceUpdateCurrentUserCache({ me: user }))
    );
  }

  updateCurrentUserLocale(variables: UpdateCurrentUserLocaleMutationVariables) {
    return this.updateCurrentUserLocaleGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserLocale)
    );
  }

  updateUserPassword(variables: UpdateUserPasswordMutationVariables) {
    return this.updateUserPasswordGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserPassword)
    );
  }

  updateNotificationsInfo(variables: UpdateNotificationsInfoMutationVariables) {
    return this.updateNotificationsInfoGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateNotifications)
    );
  }

  updateUserAdditionalConfigs(variables: UpdateUserAdditionalConfigsMutationVariables) {
    return this.updateUserAdditionalConfigsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserAdditionalConfigs)
    );
  }

  updateApiOptions(variables: UpdateApiOptionsMutationVariables) {
    return this.updateApiOptionsGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      switchMap(() => this.currentUserOrganization())
    );
  }

  setCurrentUserMissingInfo(variables: SetCurrentUserMissingInfoMutationVariables) {
    return this.setCurrentUserMissingInfoGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateUserDetails)
    );
  }

  isSubscriptionFree(user: User) {
    return user && ((user.subscription.name || '').toLowerCase() === 'free' || this.hasSubscriptionEnded(user));
  }

  isSubscriptionPaid(user: User) {
    return user && !['empresarial', 'free', 'autentique plus'].includes((user.subscription.name || '').toLowerCase()) && !this.hasSubscriptionEnded(user);
  }

  getCompany(user: SimpleUserWithOrganization) {
    return user.member && user.member.group && user.member.group.styles && user.member.group.styles.overwrite_name ? user.member.group.company || user.company : user.company;
  }

  getCompanyCnpj(user: SimpleUserWithOrganization) {
    return user.member && user.member.group && user.member.group.styles && user.member.group.styles.overwrite_name ? user.member.group.cnpj || user.cnpj : user.cnpj;
  }

  getSubscriptionName(user: User) {
    return this.isSubscriptionFree(user) ? this.translateService.instant('free') : user.subscription.name;
  }

  permissionCheck(condition: boolean) {
    if (!condition) {
      this.router.navigate(['/'], { replaceUrl: true });
      return this.notyService.error(this.translateService.instant('error_without_permission_page'));
    }
  }

  checkPremiumFeatures(event: MouseEvent, user: User) {
    if (!user || !user.subscription) {
      event.preventDefault();
    } else if (!user.subscription.has_premium_features) {
      event.preventDefault();
      this.notyService.error(this.translateService.instant('notyService.notAvailableFreePlan'));
    }
  }

  getTmpUser() {
    const currentDate = new Date();
    const lang = navigator?.language || 'en-US';
    return this.parseUser({
      data: {
        me: {
          name: '',
          email: '',
          birthday: null,
          cpf: null,
          created_at: currentDate.toJSON(),
          email_verified_at: currentDate.toJSON(),
          locale: {
            country: lang.split('-')[1] || 'US',
            date_format: lang === 'en-US' ? 'MM_DD_YYYY' : 'DD_MM_YYYY',
            language: lang,
            timezone: window.hasOwnProperty('Intl') ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Etc/UTC'
          },
          subscription: {
            name: 'Free',
            documents: 0,
            credits: 0,
            bonus: 0,
            created_at: currentDate.toJSON(),
            expires_at: new Date(new Date().setMonth(currentDate.getMonth() + 1)).toJSON()
          },
          settings: { email: {} },
          organization: { billing_customer: {}, settings: {} },
          member: {
            user: {},
            permissions: {},
            group: { permissions: {}, styles: {} }
          }
        }
      }
    } as any);
  }

  private hasSubscriptionEnded(user: User) {
    return user && user.subscription.expires_at && new Date(user.subscription.expires_at) < new Date();
  }

  private parseUser(apolloQueryResult: ApolloQueryResult<CurrentUserQuery>) {
    const response = cloneDeep(apolloQueryResult);
    const currentUser = {
      ...response.data.me,
      newHash: response.data.newHash,
      currentPermissions: get(response.data.me.member.group, 'permissions.overwrite_permissions') ? response.data.me.member.group.permissions : response.data.me.member.permissions
    } as User;
    currentUser.company = this.getCompany(currentUser);
    currentUser.cnpj = this.getCompanyCnpj(currentUser);
    currentUser.settings = currentUser.settings || {};
    currentUser.settings.format = currentUser.settings.format || SignatureFormat.Handwriting;
    currentUser.settings.initials_format = currentUser.settings.initials_format || SignatureFormat.Handwriting;
    currentUser.settings.email = currentUser.settings.email || {};
    currentUser.settings.email.template = currentUser.settings.email.template || EmailTemplates.Default;
    currentUser.settings.email.title = currentUser.settings.email.title || '{{nome}} enviou um documento para você {{funcao}}';
    currentUser.settings.email.subject = currentUser.settings.email.subject || 'Enviou um documento para você {{funcao}}';
    currentUser.settings.email.sender = currentUser.settings.email.sender || currentUser.company || currentUser.name;
    currentUser.settings.email.text = currentUser.settings.email.text || 'Por favor acesse e assine eletronicamente o documento clicando no botão acima.';
    currentUser.settings.email.image = currentUser.settings.email.image || 'https://storage.googleapis.com/user-profile-assets/autentique.png';
    currentUser.settings.email.colors = (currentUser.settings.email.colors || []).length > 0 ? currentUser.settings.email.colors : ['#3379F2', '#3379F2'];
    currentUser.locale = {
      country: currentUser.locale?.country || null,
      language: currentUser.locale?.language || null,
      timezone: currentUser.locale?.timezone || null,
      date_format: currentUser.locale?.date_format || null
    };
    return currentUser;
  }
}
